/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {AnnotationService} from './features/annotation.service';
import {SpatialUtilService} from './spatial-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {LegalParcelTypeLookup} from '../../common/models/legal-parcel-type.model';
import {of, Subject} from 'rxjs';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class FeaturesAdjustmentUtilService {
    title = 'FeaturesAdjustmentUtilService';

    constructor(
        private annotationService: AnnotationService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    adjustLotOrRoadFeatures(features: any[], refScale: any, scaleRatio: any) {
        if (features.length === 0) {
            return of(null);
        }

        const roadFeatureSubTypes = this.getLegalParcelTypeLookups()
            .filter(l => ['Road', 'Road Widening'].includes(l.description))
            .map(l => l.id);

        const annotations: any[] = features.filter(
            f => ['lot', 'road'].includes(f.attributes.featuretype) && roadFeatureSubTypes.includes(f.attributes.featuresubtype)
        );

        if (annotations.length === 0) {
            return of(null);
        }

        return forkJoinDefaultIfEmpty(
            annotations.map(annotation => {
                const polygon = this.spatialUtilService.getPolygonGraphics().find((g: any) => g.attributes.id === annotation.attributes.childid);

                if (polygon) {
                    const subject = new Subject<void>();

                    this.annotationService.getPolygonCentroid(polygon, (centroid: any) => {
                        const annotationConfigurations = this.annotationService.getAnnotationConfigurations(
                            annotation.attributes.featuretype,
                            annotation.attributes.featuresubtype
                        );

                        const ac = annotationConfigurations.find(ac => annotation.attributes[ac.annotationAttribute]);
                        if (
                            ac &&
                            !(
                                !scaleRatio &&
                                this.spatialUtilService
                                    .getAnnotations()
                                    .some(
                                        (a: any) =>
                                            a.attributes[ac.annotationAttribute] &&
                                            a.attributes.enlargementid === annotation.attributes.enlargementid &&
                                            a.attributes.parentid === annotation.attributes.parentid &&
                                            a.attributes.featureid === annotation.attributes.featureid &&
                                            a.attributes.featuretype === annotation.attributes.featuretype &&
                                            a.attributes.childtype === annotation.attributes.childtype &&
                                            a.attributes.childid === annotation.attributes.childid
                                    )
                            )
                        ) {
                            const newOffsetY = refScale * ((ac.centroidOffset || 0) - (ac.singleAnnoOffsetDistance || 0));

                            if (scaleRatio) {
                                const oldOffsetY = newOffsetY / scaleRatio;
                                annotation.geometry.y = annotation.geometry.y - oldOffsetY + newOffsetY;
                            } else {
                                annotation.geometry.x = centroid.x;
                                annotation.geometry.y = centroid.y + newOffsetY;
                            }

                            this.spatialUtilService
                                .applyEdits('annotation', (!scaleRatio && [annotation]) || null, (scaleRatio && [annotation]) || null, null)
                                .subscribe(() => {
                                    subject.next();
                                    subject.complete();
                                });
                        } else {
                            subject.next();
                            subject.complete();
                        }
                    });

                    return subject;
                }

                return this.spatialUtilService.applyEdits(
                    'annotation',
                    (!scaleRatio && [annotation]) || null,
                    (scaleRatio && [annotation]) || null,
                    null
                );
            })
        );
    }

    private getLegalParcelTypeLookups(): LegalParcelTypeLookup[] {
        return this.lookupService.getLookups('legalParcelType');
    }
}
